'use client';

import { Subject } from '@/hooks/useSubject';
import { Dialog, DialogBackdrop } from '@headlessui/react';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import { useLayoutEffect, useState } from 'react';
import {
    mdiArrowRight,
    mdiOpenInNew,
    mdiTuneVariant,
    mdiUndoVariant,
} from '@mdi/js';
import SchooltvLogo from '../Redactie/Logo/SchooltvLogo';
import TextButton from '@/components/Button/TextButton';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import Icon from '@mdi/react';
import { EmbeddableTextAccessibilitySettings } from '../AccessibilityMenu/AccessibilityMenu';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import EmojiText from '../EmojiText/EmojiText';
import { getTagValueByKey } from '@/utils/tags';

const StoryIntro = ({ subject }: { subject: Subject }) => {
    const reduceMotion = useReducedMotion();

    const { subjectProgress, setHasSeenIntro } = useSubjectProgress(subject.id);

    // Only show after first render, to fix SSR issues
    const [canShowIntro, setCanShowIntro] = useState(false);
    useLayoutEffect(() => {
        setCanShowIntro(true);
    }, []);

    const [showSettings, setShowSettings] = useState(false);

    return (
        <Dialog
            open={canShowIntro && !subjectProgress.hasSeenIntro}
            onClose={() => setHasSeenIntro(true)}
        >
            <DialogBackdrop className="fixed inset-0 bg-primary/50 filter backdrop-blur-lg z-200" />
            <div className="fixed inset-0 w-full h-full overflow-y-auto z-200">
                <div className="grid items-center justify-stretch min-h-screen px-4 py-12">
                    <AnimatePresence>
                        {!showSettings && (
                            <motion.div
                                className="row-start-1 row-end-1 col-start-1 col-end-1 max-w-sm w-full mx-auto bg-white rounded-xl shadow-2xl overflow-hidden"
                                initial={{
                                    opacity: 0,
                                    scale: 0.9,
                                    x: !reduceMotion ? '-25%' : 0,
                                }}
                                animate={{ opacity: 1, scale: 1, x: 0 }}
                                exit={{
                                    opacity: 0,
                                    scale: 0.9,
                                    x: !reduceMotion ? '-25%' : 0,
                                    transition: { duration: 0.3 },
                                }}
                            >
                                <div className="relative aspect-square overflow-hidden">
                                    {!!subject?.cover_image?.url && (
                                        <ResponsiveImage
                                            src={subject.cover_image.url}
                                            alt={subject.cover_image.alt_text}
                                            responsiveImages={
                                                subject.cover_image
                                                    .responsive_images
                                            }
                                            imageClassName="aspect-square w-full h-full"
                                            containerClassName="absolute mb-4"
                                        />
                                    )}
                                    <div className="absolute bottom-0 inset-x-0 z-10 text-center text-white bg-gradient-to-b from-primary-900/0 via-primary-800/80 to-primary-800/90 p-4">
                                        <SchooltvLogo className="mx-auto w-24 mt-16 mb-2 text-white/75" />
                                        <Dialog.Title className="text-3xl font-bold mb-2">
                                            <EmojiText>
                                                {subject.title}
                                            </EmojiText>
                                        </Dialog.Title>
                                        <Dialog.Description className="font-semibold mb-2 mx-4">
                                            <EmojiText>
                                                {subject.description}
                                            </EmojiText>
                                        </Dialog.Description>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4 p-4">
                                    <TextButton
                                        className="w-full"
                                        onClick={() => setShowSettings(true)}
                                        icon={mdiArrowRight}
                                    >
                                        Nu beginnen
                                    </TextButton>
                                    {!!getTagValueByKey(
                                        subject,
                                        'lesbrief_link',
                                    ) && (
                                        <TextButton
                                            className="w-full inner-border-secondary-text-lg-aaa/50 text-secondary-text-lg-aa"
                                            icon={mdiOpenInNew}
                                            isPrimary={false}
                                            onClick={() => {
                                                window.open(
                                                    getTagValueByKey(
                                                        subject,
                                                        'lesbrief_link',
                                                    ),
                                                    '_blank',
                                                );
                                            }}
                                        >
                                            Lesbrief
                                        </TextButton>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <AnimatePresence>
                        {showSettings && (
                            <motion.div
                                className="row-start-1 row-end-1 col-start-1 col-end-1 max-w-sm w-full mx-auto bg-white rounded-xl shadow-2xl overflow-hidden"
                                initial={{
                                    opacity: 0,
                                    scale: 0.9,
                                    x: !reduceMotion ? '25%' : 0,
                                }}
                                animate={{
                                    opacity: 1,
                                    scale: 1,
                                    x: 0,
                                    transition: { duration: 0.3 },
                                }}
                                exit={{
                                    opacity: 0,
                                    scale: 0.9,
                                    x: !reduceMotion ? '25%' : 0,
                                }}
                            >
                                <div
                                    aria-hidden="true"
                                    className="flex items-center justify-center py-8 bg-primary"
                                >
                                    <div className="p-4 bg-primary-600 text-white rounded-full">
                                        <Icon
                                            path={mdiTuneVariant}
                                            size={1.25}
                                        />
                                    </div>
                                </div>
                                <div className="p-4">
                                    <Dialog.Title className="text-3xl font-bold mb-2">
                                        Hoe lees jij het liefst?
                                    </Dialog.Title>
                                    <Dialog.Description className="font-semibold pr-2">
                                        Nog meer opties vind je hierna bij{' '}
                                        <span className="inline-block">
                                            <Icon
                                                path={mdiTuneVariant}
                                                size={0.5}
                                                className="inline-block"
                                            />{' '}
                                            instellingen.
                                        </span>{' '}
                                        Je kan ze op ieder moment aanpassen.
                                    </Dialog.Description>

                                    <div className="-mx-4">
                                        <hr className="mt-4" />
                                        <EmbeddableTextAccessibilitySettings variant="light" />
                                        <hr className="mb-4" />
                                    </div>

                                    <TextButton
                                        className="w-full"
                                        onClick={() => {
                                            setHasSeenIntro(true);
                                            setShowSettings(false);
                                        }}
                                        icon={mdiArrowRight}
                                    >
                                        Opslaan
                                    </TextButton>
                                    <TextButton
                                        className="w-full mt-4 inner-border-secondary-text-lg-aaa/50 text-secondary-text-lg-aa"
                                        onClick={() => setShowSettings(false)}
                                        icon={mdiUndoVariant}
                                        isPrimary={false}
                                    >
                                        Terug
                                    </TextButton>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </Dialog>
    );
};

export default StoryIntro;
